import React, { useState } from 'react';
import { Send, CheckCircle, XCircle } from 'lucide-react'; // For form submission status icons

const GetAQuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: [], // This will hold an array of selected services
    message: ''
  });
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', null

  // --- THESE ARE YOUR ACTUAL GOOGLE FORM ENTRY IDS ---
  // Confirmed from your provided URL for Get A Quote form:
  // https://docs.google.com/forms/d/e/1FAIpQLSf9w6Jef8f7JYxp5vZ0ZaId_ZBstIsniH4ZXogZCgznKSPZzA/viewform?usp=pp_url&entry.134874337=firstname+secondname&entry.1112110695=example@gmail.com&entry.883369640=0794255000&entry.1694842534=Graphics+%26+Design&entry.1694842534=Data+Analysis&entry.1694842534=Virtual+Assistance&entry.1694842534=Accounting+%26+Bookkeeping&entry.1694842534=Other&entry.1378552054=here+what+you+want
  const GOOGLE_FORM_ENTRY_IDS = {
    name: 'entry.134874337',       // Maps to 'Your Name'
    email: 'entry.1112110695',     // Maps to 'Your Email'
    phone: 'entry.883369640',      // Maps to 'Phone Number'
    service: 'entry.1694842534',   // Maps to 'Services You\'re Interested In' (checkboxes)
    message: 'entry.1378552054'    // Maps to 'Your Message / Project Details'
  };
  // ----------------------------------------------------

  // The Google Form 'formResponse' URL for the Get A Quote form
  const GOOGLE_FORM_ACTION_URL = "https://docs.google.com/forms/d/e/1FAIpQLSf9w6Jef8f7JYxp5vZ0ZaId_ZBstIsniH4ZXogZCgznKSPZzA/formResponse";

  const servicesOffered = [
    "Graphics & Design",
    "Data Analysis",
    "Virtual Assistance",
    "Accounting & Bookkeeping",
    "Kids Hub Programs",
    "Other"
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prevData => ({
        ...prevData,
        service: checked
          ? [...prevData.service, value]
          : prevData.service.filter(service => service !== value)
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus(null); // Reset status

    // Client-side validation: Check if required fields are filled
    if (!formData.name || !formData.email || formData.service.length === 0 || !formData.message) {
      setSubmissionStatus('error'); // Set error if validation fails
      return; // Stop the submission process
    }

    // Prepare data for Google Forms using FormData
    const dataToSubmit = new FormData();
    dataToSubmit.append(GOOGLE_FORM_ENTRY_IDS.name, formData.name);
    dataToSubmit.append(GOOGLE_FORM_ENTRY_IDS.email, formData.email);

    // Only append phone if it has a value
    if (formData.phone) {
      dataToSubmit.append(GOOGLE_FORM_ENTRY_IDS.phone, formData.phone);
    }

    // Append each selected service. Google Forms handles multiple values for the same entry ID.
    formData.service.forEach(service => {
      dataToSubmit.append(GOOGLE_FORM_ENTRY_IDS.service, service);
    });

    dataToSubmit.append(GOOGLE_FORM_ENTRY_IDS.message, formData.message);

    try {
      // Send the POST request directly to Google Forms with 'no-cors' mode
      const response = await fetch(GOOGLE_FORM_ACTION_URL, {
        method: 'POST',
        mode: 'no-cors', // Crucial for client-side submission to Google Forms
        body: dataToSubmit,
        // The browser automatically sets 'Content-Type' when using FormData.
      });

      // In 'no-cors' mode, response.ok will always be true and status will be 0.
      // We assume success if the fetch operation itself doesn't throw a network error.
      setSubmissionStatus('success');
      // Clear form data after successful submission
      setFormData({ name: '', email: '', phone: '', service: [], message: '' });

    } catch (error) {
      // Catch network errors (e.g., no internet connection)
      console.error('Network or submission error:', error);
      setSubmissionStatus('error');
    }
  };

  // --- Sample Products and Testimonials sections remain unchanged ---
  const sampleProducts = [
    {
      id: 1,
      category: 'Graphics & Design',
      title: 'Brand Identity Kit',
      description: 'A comprehensive branding package including logo design, color palette, typography guidelines, and brand usage manual.',
      imageUrl: 'https://placehold.co/400x250/C9B072/0A1128?text=Brand+Kit+Design',
    },
    {
      id: 2,
      category: 'Data Analysis',
      title: 'Sales Performance Dashboard',
      description: 'Interactive dashboard visualizing key sales metrics, trends, and forecasts for strategic decision-making.',
      imageUrl: 'https://placehold.co/400x250/4CAF50/F8F8F8?text=Sales+Dashboard',
    },
    {
      id: 3,
      category: 'Virtual Assistance',
      title: 'Executive Support Package',
      description: 'Dedicated virtual assistant services for scheduling, email management, travel arrangements, and administrative tasks.',
      imageUrl: 'https://placehold.co/400x250/0A1128/C9B072?text=VA+Support',
    },
    {
      id: 4,
      category: 'Graphics & Design',
      title: 'Social Media Content Pack',
      description: 'Custom designed graphics and templates for consistent and engaging social media presence across platforms.',
      imageUrl: 'https://placehold.co/400x250/F8F8F8/0A1128?text=Social+Media+Graphics',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans py-20">
      <div className="container mx-auto px-6">
        {/* Get a Quote Section */}
        <section className="mb-20 bg-[#1A2238] p-8 rounded-xl shadow-lg">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-6 text-[#C9B072] text-center">Get a Personalized Quote</h1>
          <p className="text-xl text-[#CCD2E3] text-center mb-10 max-w-3xl mx-auto">
            Tell us about your needs, and we'll provide a tailored quote for our services.
          </p>

          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-lg font-medium text-[#F8F8F8] mb-2">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-3 rounded-md bg-[#0A1128] border border-[#C9B072] text-[#F8F8F8] focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                placeholder="Enter your name"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-lg font-medium text-[#F8F8F8] mb-2">Your Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 rounded-md bg-[#0A1128] border border-[#C9B072] text-[#F8F8F8] focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="phone" className="block text-lg font-medium text-[#F8F8F8] mb-2">Phone Number (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-3 rounded-md bg-[#0A1128] border border-[#C9B072] text-[#F8F8F8] focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                placeholder="e.g., +254 7XX XXX XXX (Kilifi)" // Updated placeholder
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-lg font-medium text-[#F8F8F8] mb-2">Services You're Interested In</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {servicesOffered.map((service) => (
                  <label key={service} className="flex items-center text-lg text-[#CCD2E3] cursor-pointer">
                    <input
                      type="checkbox"
                      name="service"
                      value={service}
                      checked={formData.service.includes(service)}
                      onChange={handleChange}
                      className="form-checkbox h-5 w-5 text-[#4CAF50] rounded-md border-gray-300 focus:ring-[#4CAF50]"
                    />
                    <span className="ml-2">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="message" className="block text-lg font-medium text-[#F8F8F8] mb-2">Your Message / Project Details</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                className="w-full p-3 rounded-md bg-[#0A1128] border border-[#C9B072] text-[#F8F8F8] focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                placeholder="Describe your project or specific needs..."
                required
              ></textarea>
            </div>

            <div className="md:col-span-2 flex justify-center mt-4">
              <button
                type="submit"
                className="px-10 py-4 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-xl rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 flex items-center justify-center"
              >
                <Send size={24} className="mr-3" /> Send Request
              </button>
            </div>

            {submissionStatus === 'success' && (
              <div className="md:col-span-2 text-center mt-4 p-4 bg-green-700 text-white rounded-md flex items-center justify-center">
                <CheckCircle size={24} className="mr-2" />
                <span>Your quote request has been sent successfully! We will get back to you soon.</span>
              </div>
            )}
            {submissionStatus === 'error' && (
              <div className="md:col-span-2 text-center mt-4 p-4 bg-red-700 text-white rounded-md flex items-center justify-center">
                <XCircle size={24} className="mr-2" />
                <span>There was an error sending your request. Please fill in all required fields and try again.</span>
              </div>
            )}
          </form>
        </section>

        {/* Sample Products Section */}
        <section className="mb-20 bg-[#E0E0E0] p-8 rounded-xl shadow-lg"> {/* Added gray background here */}
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-12 text-[#0A1128] text-center">Our Sample Work & Products</h2> {/* Changed text color for contrast */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sampleProducts.map(product => (
              <div key={product.id} className="bg-[#1A2238] rounded-xl shadow-lg overflow-hidden transform transition-transform duration-300 hover:scale-105">
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  className="w-full h-48 object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/400x250/E0E0E0/333333?text=Image+Error`; }}
                />
                <div className="p-6">
                  <span className="text-sm font-semibold text-[#C9B072] mb-2 block">{product.category}</span>
                  <h3 className="text-2xl font-bold text-[#F8F8F8] mb-3">{product.title}</h3>
                  <p className="text-[#CCD2E3] text-base leading-relaxed">{product.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials Section - Changed to "Coming Soon" */}
        <section>
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-12 text-[#F8F8F8] text-center">What Our Clients Say</h2>
          <div className="bg-[#1A2238] p-8 rounded-xl shadow-lg text-center">
            <p className="text-3xl font-semibold text-[#C9B072]">Coming Soon!</p>
            <p className="text-xl text-[#CCD2E3] mt-4">We are gathering feedback from our amazing clients in Kilifi. Check back soon for testimonials!</p>
             <p className="text-md text-[#CCD2E3] mt-2">Location: Kilifi, Kilifi County, Kenya</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GetAQuotePage;
